import { collection, getDocs, limit, orderBy, query, Timestamp } from "firebase/firestore";
import { db } from "@/firebase/config";
import type { FeedRepository } from "@/modules/leagues/repositories/FeedRepository";
import type { FeedEvent, FeedEventType } from "@/modules/leagues/types/feed";

/**
 * Feed REAL (Firestore) — reaproveita a MESMA coleção `history` já usada
 * pelas ações administrativas (Configurações Gerais, Importar Pontos) e
 * pela entrada de participantes na liga. Nunca cria uma segunda estrutura
 * paralela: o cron e o FirestoreDuelRepository escrevem nessa mesma
 * coleção pros novos tipos de evento (duelo, MVP da rodada, gol).
 *
 * Tolerante a entradas antigas: registros gravados antes desses novos
 * tipos existirem (`type: "rule_frozen"`/`"rule_updated"`, usados de forma
 * genérica) continuam aparecendo no feed — só caem no tipo mais próximo
 * em vez de sumir ou quebrar a tela.
 */
export class FirestoreFeedRepository implements FeedRepository {
  async getFeed(leagueId: string): Promise<FeedEvent[]> {
    const q = query(collection(db, "groups", leagueId, "history"), orderBy("createdAt", "desc"), limit(50));
    const snap = await getDocs(q);
    return snap.docs.map((d) => {
      const data = d.data() as { type?: string; descricao?: string; createdAt?: Timestamp };
      return {
        id: d.id,
        type: normalizeType(data.type),
        message: data.descricao ?? "Atividade na liga.",
        createdAt: data.createdAt ? data.createdAt.toDate().toISOString() : new Date().toISOString(),
      };
    });
  }
}

const KNOWN_TYPES = new Set<FeedEventType>([
  "participant_joined",
  "settings_changed",
  "invite_accepted",
  "round_released",
  "matches_finished",
  "score_updated",
  "bonus_added",
  "challenge_created",
  "duel_accepted",
  "duel_resolved",
  "round_mvp",
]);

function normalizeType(raw: string | undefined): FeedEventType {
  if (raw && KNOWN_TYPES.has(raw as FeedEventType)) return raw as FeedEventType;
  // Entradas antigas (`rule_frozen`/`rule_updated`, usadas genericamente
  // antes desta etapa) caem aqui — trata como "configuração da liga",
  // categoria mais próxima do que representavam.
  return "settings_changed";
}
